import { getIdTokenResult, type User } from 'firebase/auth';

import { firebase, type UserCustomClaims } from '@gitbook/app-database';
import { Trace } from '@gitbook/util-debug';
import type { MandateProps } from '@gitbook/util-ts';

/**
 * Map of user ids to intercom user hashes to prevent calling the method multiple times.
 */
const userHashesMap = new Map<string, Promise<string | null>>();

/**
 * Get the intercom user hash for a given user in the app.
 * Calls the users-computeIntercomHash function if the hash is not already available in the claims.
 */
export async function getIntercomUserHash(user: User): Promise<string>;
export async function getIntercomUserHash(user: null): Promise<null>;
export async function getIntercomUserHash(user: User | null): Promise<string | null> {
    if (!user) {
        return null;
    }

    const userHash = userHashesMap.get(user.uid);
    if (userHash) {
        return userHash;
    }

    const pending = (async () => {
        const trace = new Trace('analytics.getIntercomUserHash');
        trace.label('userId', user.uid);

        return trace.wrap(async () => {
            // Use the already computed intercomUserHash if available in the claims
            // or call a function to compute it and store it in the claims
            let hashFromToken: string | null = null;
            try {
                hashFromToken = await getHashFromToken(user);
            } catch (error) {
                // Ignore error, as the users might have to reauthenticate
                // if their token is expired.
                // In this case, we compute the token from the function.
                trace.label('error', error.message);
            }

            if (hashFromToken) {
                trace.label('hasHashInToken', true);
                return hashFromToken;
            }

            const computeIntercomHash = firebase
                .functions()
                .httpsCallable('users-computeIntercomHash');
            const result = await computeIntercomHash();
            const { intercomUserHash } = result.data as Pick<
                MandateProps<UserCustomClaims, 'intercomUserHash'>,
                'intercomUserHash'
            >;
            return intercomUserHash;
        });
    })();

    userHashesMap.set(user.uid, pending);
    return pending;
}

/**
 * Try to get the hash from the user's token using the current claims.
 * If the hash is not available, try to get it from a fresh token.
 */
async function getHashFromToken(user: User): Promise<string | null> {
    // First we try to get the hash from the current token to prevent
    // refetching a new one, which is slow.
    try {
        const idTokenResult = await getIdTokenResult(user);
        const claims = idTokenResult.claims as UserCustomClaims;
        if (claims.intercomUserHash) {
            return claims.intercomUserHash;
        }
    } catch (_err) {
        /**/
    }

    try {
        const freshIdTokenResult = await getIdTokenResult(user, true);
        const claims = freshIdTokenResult.claims as UserCustomClaims;
        if (claims.intercomUserHash) {
            return claims.intercomUserHash;
        }
    } catch (_err) {
        /**/
    }

    return null;
}
